import { inject } from "@angular/core";
import { CanActivateFn } from "@angular/router";
import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { ConfigService } from "../services/config/config.service";
import { SettingsService } from "../services/settings/settings.service";

/** Resolve before child pages read settings, including direct links to briefing tabs. */
export const baseSelectionGuard: CanActivateFn = async (route, state) => {
  const settings = inject(SettingsService);
  const config = inject(ConfigService);
  const alerts = inject(AlertController);
  const translate = inject(TranslateService);
  const requested = route.queryParamMap.get("base");
  const briefing = /^\/base-briefing(?:[/?#]|$)/.test(state.url);
  if (requested === null && !briefing) return true;

  await settings.loaded;
  const bases = (await config.configP).bases.filter((base) => base.name?.trim());
  const find = (name: string) => bases.find((base) => base.name.toUpperCase() === name.trim().toUpperCase());
  const fromUrl = requested === null ? undefined : find(requested);
  if (fromUrl) {
    await settings.changeSetting("base", fromUrl);
    return true;
  }
  const saved = await settings.getSetting("base");
  // An invalid explicit URL must not silently use a different saved base.
  if (requested === null && find(saved?.name ?? "")) return true;
  if (!briefing) return true;

  const alert = await alerts.create({
    header: translate.instant("Select base"),
    message: translate.instant("Please select a base for the base briefing."),
    backdropDismiss: false,
    inputs: bases.map((base) => ({ type: "radio", label: base.name.toUpperCase(), value: base.name })),
    buttons: [
      { text: translate.instant("Cancel"), role: "cancel" },
      {
        text: translate.instant("Select"),
        role: "confirm",
        handler: (name: string) => !!find(name ?? ""),
      },
    ],
  });
  await alert.present();
  const result = await alert.onDidDismiss();
  const selected = find(result.data?.values ?? "");
  if (result.role !== "confirm" || !selected) return false;
  await settings.changeSetting("base", selected);
  return true;
};
